import { IAction, IActivity } from 'core/models/Models'
import { produce } from 'immer'
import {
  ACTION,
  ACTION_ADD_REMOVE_PARTICIPANT,
  ACTION_CHECK_LIST_ITEMS,
  ACTION_COMMENT_LIKE,
  ACTION_COMMENTS,
  ACTION_DOCUMENTS,
  ACTION_LIKE,
  ACTION_PARTICIPANTS,
  ACTION_REMOVE_PARTICIPANT,
  ACTION_TAGS,
  ACTION_VIEW,
  ACTION_VOTE,
  ALL_ACTIONS,
  CURRENT_ACTIONS_PAGINATED,
  JOIN_ACTION,
  NEW_ACTION,
  PAST_ACTIONS_PAGINATED,
  SAVED_ACTIONS_PAGINATED,
  SEARCH_ACTION,
  UPDATE_ACTION,
  UPDATE_NEW_ACTION,
  UPDATE_NEW_ACTION_DOCUMENT,
  UPDATE_NEW_ACTION_PARTICIPANTS,
  UPDATE_NEW_ACTION_TAGS,
  UPDATE_NEW_ACTION_CHECK_LIST_ITEM,
  USERS_ACTIONS_PAGINATED,
  ADD_NEW_ACTION_CHECK_LIST_ITEM,
  ADD_NEW_ACTION_DOCUMENT,
  DELETE_NEW_ACTION_CHECK_LIST_ITEM,
  DELETE_NEW_ACTION_DOCUMENT,
} from 'core/actions/ActionActions'

const emptyAction: IAction = {
  attributes: {
    pId: 0,
    id: '',
    name: '',
    imageUrl: '',
    description: '',
    endDate: '',
    published: false,
    likedByIds: [],
    seenByIds: [],
  },
  participants: [],
  tags: [],
  checkListItems: [],
  documents: [],
  comments: [],
  history: [],
}

export interface IActionState {
  newAction: IAction
  actions: IAction[]
  currentActions: IAction[]
  pastActions: IAction[]
  usersActions: IAction[]
  savedActions: IAction[]
  actionsLoaded: boolean
  isFinalPage: {
    current: boolean
    past: boolean
    users: boolean
    saved: boolean
  }
}

const initialState: IActionState = {
  newAction: emptyAction,
  actions: [],
  currentActions: [],
  pastActions: [],
  usersActions: [],
  savedActions: [],
  actionsLoaded: false,
  isFinalPage: {
    current: false,
    past: false,
    users: false,
    saved: false,
  },
}

const actionCollectionKeys: Array<keyof Pick<IActionState, 'actions' | 'currentActions' | 'pastActions' | 'usersActions' | 'savedActions'>> = [
  'actions',
  'currentActions',
  'pastActions',
  'usersActions',
  'savedActions',
]

function appendUniqueActions(existingActions: IAction[], newActions: IAction[]) {
  const existingIds = new Set(existingActions.map((item) => item?.attributes?.id))
  const uniqueActions = newActions.filter((item) => !existingIds.has(item?.attributes?.id))
  return [...existingActions, ...uniqueActions]
}

function replaceOrAppendAction(actions: IAction[], payload: IAction) {
  const existingIndex = actions.findIndex((item) => item?.attributes?.id == payload?.attributes?.id)
  if (existingIndex === -1) {
    return [...actions, payload]
  }
  return actions.map((item) => (item?.attributes?.id == payload?.attributes?.id ? payload : item))
}

function replaceActionIfPresent(actions: IAction[], payload: IAction) {
  const existingIndex = actions.findIndex((item) => item?.attributes?.id == payload?.attributes?.id)
  if (existingIndex === -1) {
    return actions
  }
  return actions.map((item) => (item?.attributes?.id == payload?.attributes?.id ? payload : item))
}

function removeAction(actions: IAction[], actionId: number) {
  return actions.filter((item) => item?.attributes?.pId !== actionId)
}

function updateActionCollections(
  state: IActionState,
  draft: IActionState,
  updater: (actions: IAction[]) => IAction[],
) {
  actionCollectionKeys.forEach((key) => {
    draft[key] = updater(state[key])
  })
}

function updateComments(actions: IAction[], payload, updater: (action: IAction) => IAction) {
  return actions.map((action) => {
    if (action?.attributes?.pId === payload?.elementId || action?.attributes?.pId === payload?.newComment?.elementId) {
      return updater(action)
    }
    return action
  })
}

const actionReducer = (state = initialState, action: IActivity): IActionState =>
  produce(state, (draft) => {
    const { type, payload } = action

    switch (type) {
      case ALL_ACTIONS.GET.SUCCESS:
        draft.actionsLoaded = true
        draft.actions = payload
        break
      case SEARCH_ACTION.GET.SUCCESS:
        draft.actions = payload
        draft.actionsLoaded = false
        break
      case CURRENT_ACTIONS_PAGINATED.GET.SUCCESS:
        draft.currentActions = appendUniqueActions(state.currentActions, payload.payload)
        draft.isFinalPage.current = payload.isFinalPage
        break
      case PAST_ACTIONS_PAGINATED.GET.SUCCESS:
        draft.pastActions = appendUniqueActions(state.pastActions, payload.payload)
        draft.isFinalPage.past = payload.isFinalPage
        break
      case USERS_ACTIONS_PAGINATED.GET.SUCCESS:
        draft.usersActions = appendUniqueActions(state.usersActions, payload.payload)
        draft.isFinalPage.users = payload.isFinalPage
        break
      case SAVED_ACTIONS_PAGINATED.GET.SUCCESS:
        draft.savedActions = appendUniqueActions(state.savedActions, payload.payload)
        draft.isFinalPage.saved = payload.isFinalPage
        break
      case ACTION.POST.SUCCESS:
        draft.actions = [payload, ...state.actions]
        if (payload?.attributes?.published) {
          draft.currentActions = [payload, ...state.currentActions]
        }
        draft.usersActions = [payload, ...state.usersActions]
        draft.newAction = { ...state.newAction, ...emptyAction }
        break
      case ACTION.DELETE.SUCCESS:
        updateActionCollections(state, draft, (actions) => removeAction(actions, payload?.id))
        break
      case ACTION.GET.SUCCESS:
      case UPDATE_ACTION.POST.SUCCESS:
      case ACTION_PARTICIPANTS.POST.SUCCESS:
      case ACTION_CHECK_LIST_ITEMS.POST.SUCCESS:
      case ACTION_CHECK_LIST_ITEMS.PUT.SUCCESS:
      case ACTION_CHECK_LIST_ITEMS.DELETE.SUCCESS:
      case ACTION_DOCUMENTS.POST.SUCCESS:
      case ACTION_DOCUMENTS.PUT.SUCCESS:
      case ACTION_DOCUMENTS.DELETE.SUCCESS:
      case ACTION_VIEW.POST.SUCCESS:
      case ACTION_ADD_REMOVE_PARTICIPANT.POST.SUCCESS:
      case ACTION_VOTE.POST.SUCCESS:
      case JOIN_ACTION.POST.SUCCESS:
      case ACTION_REMOVE_PARTICIPANT.POST.SUCCESS:
      case ACTION_TAGS.POST.SUCCESS:
        draft.actions = replaceOrAppendAction(state.actions, payload)
        draft.currentActions = replaceActionIfPresent(state.currentActions, payload)
        draft.pastActions = replaceActionIfPresent(state.pastActions, payload)
        draft.usersActions = replaceActionIfPresent(state.usersActions, payload)
        draft.savedActions = replaceActionIfPresent(state.savedActions, payload)
        break
      case ACTION_COMMENTS.POST.SUCCESS:
        updateActionCollections(state, draft, (actions) =>
          updateComments(actions, payload, (currentAction) => {
            const newComments = [...currentAction.comments]
            const newHistory = [...currentAction.history]
            newComments.push(payload?.newComment)
            newHistory.push(payload?.history)
            return { ...currentAction, comments: newComments, history: newHistory }
          }),
        )
        break
      case ACTION_COMMENTS.PUT.SUCCESS:
        updateActionCollections(state, draft, (actions) =>
          updateComments(actions, payload, (currentAction) => ({
            ...currentAction,
            comments: currentAction.comments.map((comment) => {
              return comment.id === payload?.id ? payload : comment
            }),
          })),
        )
        break
      case ACTION_COMMENTS.DELETE.SUCCESS:
        updateActionCollections(state, draft, (actions) =>
          updateComments(actions, payload?.comment, (currentAction) => {
            const newHistory = [...currentAction.history, payload?.history]
            return {
              ...currentAction,
              comments: currentAction.comments.filter(
                (comment) => comment?.id !== payload?.comment?.id && comment?.parentId != payload?.comment?.id,
              ),
              history: newHistory,
            }
          }),
        )
        break
      case ACTION_LIKE.POST.SUCCESS:
        updateActionCollections(state, draft, (actions) =>
          actions.map((currentAction) => {
            if (currentAction.attributes?.id !== payload?.actionId) {
              return currentAction
            }

            const likedByIds = currentAction.attributes?.likedByIds
              ? [...currentAction.attributes.likedByIds]
              : []
            const pUserId = Number(payload.userId)
            const existingIndex = likedByIds.indexOf(pUserId)

            if (existingIndex > -1) {
              likedByIds.splice(existingIndex, 1)
            } else {
              likedByIds.push(pUserId)
            }

            return {
              ...currentAction,
              attributes: { ...currentAction.attributes, likedByIds },
            }
          }),
        )
        break
      case ACTION_COMMENT_LIKE.POST.SUCCESS:
        updateActionCollections(state, draft, (actions) =>
          actions.map((currentAction) => {
            if (currentAction.attributes?.pId !== payload?.actionId) {
              return currentAction
            }

            return {
              ...currentAction,
              comments: currentAction.comments.map((comment) => {
                if (comment?.pId !== payload?.commentId) {
                  return comment
                }

                const likedByIds = comment?.likedByIds ? [...comment.likedByIds] : []
                const pUserId = Number(payload.userId)
                const existingIndex = likedByIds.indexOf(pUserId)

                if (existingIndex > -1) {
                  likedByIds.splice(existingIndex, 1)
                } else {
                  likedByIds.push(pUserId)
                }

                return { ...comment, likedByIds }
              }),
            }
          }),
        )
        break
      case NEW_ACTION: {
        if (payload?.organizationId === null || payload?.organizationId === undefined) {
          delete payload['organizationId']
        }
        const newAttributes = {
          ...emptyAction.attributes,
          ...payload,
        }
        draft.newAction = { ...emptyAction, attributes: newAttributes }
        break
      }
      case UPDATE_NEW_ACTION: {
        const payloadWithoutEmptyKeys = Object.entries(payload).reduce(
          (a, [k, v]) => (v === null || v === undefined ? a : ((a[k] = v), a)),
          {},
        )
        draft.newAction.attributes = { ...state.newAction.attributes, ...payloadWithoutEmptyKeys }
        break
      }
      case UPDATE_NEW_ACTION_PARTICIPANTS:
        draft.newAction.participants = payload
        break
      case UPDATE_NEW_ACTION_TAGS:
        draft.newAction.tags = payload
        break
      case ADD_NEW_ACTION_CHECK_LIST_ITEM:
        draft.newAction.checkListItems.push(payload)
        break
      case UPDATE_NEW_ACTION_CHECK_LIST_ITEM:
        draft.newAction.checkListItems = state.newAction.checkListItems.map((item) => {
          return item.id === payload.id ? payload : item
        })
        break
      case DELETE_NEW_ACTION_CHECK_LIST_ITEM:
        draft.newAction.checkListItems = state.newAction.checkListItems.filter((item) => {
          return item.id !== payload.id
        })
        break
      case ADD_NEW_ACTION_DOCUMENT:
        draft.newAction.documents.push(payload)
        break
      case UPDATE_NEW_ACTION_DOCUMENT:
        draft.newAction.documents = state.newAction.documents.map((item) => {
          return item.id === payload.id ? payload : item
        })
        break
      case DELETE_NEW_ACTION_DOCUMENT:
        draft.newAction.documents = state.newAction.documents.filter((item) => {
          return item.id !== payload.id
        })
        break
      default:
        return draft
    }
  })

export default actionReducer
